import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Copy, Plus, LogOut, Users, Eye, FileText, BarChart3, Settings as SettingsIcon, Shield, GitCompare, CalendarDays, X, Trash2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const P = 'var(--brand)';
const PH = 'var(--brand-dark)';
const BG = '#F9F9F6';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  // What happened to the invitation email, so the creator is not left guessing.
  const [inviteEmail, setInviteEmail] = useState(null);
  // Which assessment the delete dialog is asking about, or null when closed.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [managers, setManagers] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVersion, setFilterVersion] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    candidate_name: '', candidate_email: '', role_applied_for: '',
    version: 'junior', department: '', assigned_manager: '', expiry_days: '7',
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchAssessments();
    if (user?.role === 'hr_admin') fetchManagers();
  }, [dateFrom, dateTo]);

  const fetchAssessments = async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.listAssessments(params);
      setAssessments(res.data);
    } catch { toast.error('Failed to load assessments'); }
    finally { setLoading(false); }
  };

  const fetchManagers = async () => {
    try {
      const res = await api.listUsers();
      setManagers(res.data.filter(u => u.role === 'hiring_manager'));
    } catch {}
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.candidate_email) payload.candidate_email = null;
      if (!payload.department) payload.department = null;
      if (!payload.assigned_manager) payload.assigned_manager = null;
      payload.expiry_days = parseInt(payload.expiry_days) || 7;
      const res = await api.createAssessment(payload);
      const link = `${window.location.origin}/assess/${res.data.invite_token}`;
      setInviteLink(link);
      setInviteEmail({ address: res.data.candidate_email || '', sent: Boolean(res.data.email_sent) });
      toast.success('Assessment created!');
      fetchAssessments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create assessment');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deleteAssessment(deleteTarget.id);
      toast.success(`Deleted ${deleteTarget.candidate_name}`);
      setDeleteTarget(null);
      fetchAssessments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete this assessment');
    } finally {
      setDeleting(false);
    }
  };

  const copyLink = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied!');
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length >= 6 ? (toast.error('Max 6 candidates'), prev) : [...prev, id]);
  };

  const handleCompare = () => {
    if (selectedIds.length < 2) { toast.error('Select at least 2 completed candidates'); return; }
    navigate(`/compare?ids=${selectedIds.join(',')}`);
  };

  const completedIds = new Set(assessments.filter(a => a.status === 'completed').map(a => a.id));

  const getStatusBadge = (status) => {
    const s = {
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
      completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      expired: 'bg-neutral-100 text-neutral-500 border-neutral-200',
    };
    return <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${s[status] || s.pending}`}>{status.replace('_', ' ')}</span>;
  };

  const getRecommendationBadge = (a) => {
    if (a.status !== 'completed') return null;
    // We'll show a colored dot, actual verdict comes from report
    return <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 ml-2" />;
  };

  const filtered = assessments.filter(a => {
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    if (filterVersion !== 'all' && a.version !== filterVersion) return false;
    if (searchQuery && !a.candidate_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !a.role_applied_for.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: assessments.length,
    completed: assessments.filter(a => a.status === 'completed').length,
    pending: assessments.filter(a => a.status === 'pending' || a.status === 'in_progress').length,
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-neutral-200 p-6 z-30">
        <div className="mb-8">
          <img src="/wildnet-logo.png" alt="Wildnet Technologies" className="h-7 w-auto" />
          <p className="text-xs text-neutral-500 mt-1.5">Candidate Assessment Portal</p>
        </div>
        <nav className="space-y-1">
          <button onClick={() => navigate('/dashboard')} className="w-full flex items-center gap-3 px-3 py-2 rounded-md bg-[#E8F0EC] text-brand-dark font-medium text-sm" data-testid="nav-dashboard">
            <BarChart3 className="h-4 w-4" /> Dashboard
          </button>
          {user?.role === 'hr_admin' && (
            <>
              <button onClick={() => navigate('/admin/users')} className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-neutral-50 text-neutral-600 text-sm" data-testid="nav-users">
                <Users className="h-4 w-4" /> User Management
              </button>
              <button onClick={() => navigate('/admin/settings')} className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-neutral-50 text-neutral-600 text-sm" data-testid="nav-settings">
                <SettingsIcon className="h-4 w-4" /> Settings
              </button>
              <button onClick={() => navigate('/admin/audit')} className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-neutral-50 text-neutral-600 text-sm" data-testid="nav-audit">
                <Shield className="h-4 w-4" /> Audit Log
              </button>
            </>
          )}
        </nav>
        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-3 bg-neutral-50 rounded-md mb-3 overflow-hidden">
            <p className="text-xs font-medium text-neutral-800 truncate" title={user?.full_name}>{user?.full_name}</p>
            {/* Work addresses are routinely wider than the sidebar, so trim it
                and keep the full value on hover rather than letting it spill. */}
            <p className="text-xs text-neutral-500 truncate" title={user?.email}>{user?.email}</p>
            <p className="text-[10px] text-brand uppercase tracking-widest mt-1 font-bold truncate">{user?.role?.replace('_', ' ')}</p>
          </div>
          <Button onClick={logout} variant="outline" className="w-full rounded-md text-sm" data-testid="logout-button">
            <LogOut className="h-3.5 w-3.5 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h2 className="text-3xl font-bold text-neutral-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="dashboard-title">Assessments</h2>
              <p className="text-neutral-500 mt-1 text-sm">Manage candidate assessments and view reports</p>
            </div>
            <div className="flex gap-2">
              {selectedIds.length >= 2 && (
                <Button onClick={handleCompare} variant="outline" className="rounded-md border-brand text-brand hover:bg-[#E8F0EC]" data-testid="compare-button">
                  <GitCompare className="h-4 w-4 mr-2" /> Compare ({selectedIds.length})
                </Button>
              )}
              <Dialog open={showCreateModal} onOpenChange={(open) => { setShowCreateModal(open); if (!open) { setInviteLink(''); setInviteEmail(null); setFormData({ candidate_name: '', candidate_email: '', role_applied_for: '', version: 'junior', department: '', assigned_manager: '', expiry_days: '7' }); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-brand hover:bg-brand-dark text-white rounded-md" data-testid="create-assessment-button">
                    <Plus className="h-4 w-4 mr-2" /> New Assessment
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Create New Assessment</DialogTitle>
                </DialogHeader>
                {inviteLink ? (
                  <div className="py-4">
                    {!inviteEmail?.address ? (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md mb-4">
                        <p className="text-sm font-medium text-amber-900">Created - but no email was sent</p>
                        <p className="text-xs text-amber-800 mt-1">
                          No email address was entered, so nothing was sent to the candidate.
                          Copy the link below and send it to them yourself.
                        </p>
                      </div>
                    ) : inviteEmail.sent ? (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md mb-4">
                        <p className="text-sm font-medium text-emerald-800">
                          Invitation emailed to {inviteEmail.address}
                        </p>
                        <p className="text-xs text-emerald-700 mt-1">
                          Ask them to check spam if it has not arrived. The same link is below.
                        </p>
                      </div>
                    ) : (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-4">
                        <p className="text-sm font-medium text-red-800">
                          Created - but the email to {inviteEmail.address} failed
                        </p>
                        <p className="text-xs text-red-700 mt-1">
                          Send the link below manually, and tell your admin so email can be fixed.
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input value={inviteLink} readOnly className="rounded-md text-sm" data-testid="invite-link-input" />
                      <Button onClick={() => copyLink(inviteLink)} className="bg-brand hover:bg-brand-dark rounded-md" data-testid="copy-link-button">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleCreate} className="space-y-4 py-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-sm">Candidate Name *</Label>
                        <Input value={formData.candidate_name} onChange={(e) => setFormData({...formData, candidate_name: e.target.value})} required className="rounded-md" data-testid="candidate-name-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Email (optional)</Label>
                        <Input type="email" value={formData.candidate_email} onChange={(e) => setFormData({...formData, candidate_email: e.target.value})} className="rounded-md" data-testid="candidate-email-input" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-sm">Role *</Label>
                        <Input value={formData.role_applied_for} onChange={(e) => setFormData({...formData, role_applied_for: e.target.value})} required className="rounded-md" data-testid="role-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Version *</Label>
                        <Select value={formData.version} onValueChange={(v) => setFormData({...formData, version: v})}>
                          <SelectTrigger className="rounded-md" data-testid="version-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="junior">Junior / Mid-Level (40-45 Q)</SelectItem>
                            <SelectItem value="senior">Senior / Leadership (50-55 Q)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-sm">Department</Label>
                        <Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} className="rounded-md" data-testid="department-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Link Expiry (days)</Label>
                        <Select value={formData.expiry_days} onValueChange={(v) => setFormData({...formData, expiry_days: v})}>
                          <SelectTrigger className="rounded-md" data-testid="expiry-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 day</SelectItem>
                            <SelectItem value="3">3 days</SelectItem>
                            <SelectItem value="7">7 days (default)</SelectItem>
                            <SelectItem value="14">14 days</SelectItem>
                            <SelectItem value="30">30 days</SelectItem>
                            <SelectItem value="60">60 days</SelectItem>
                            <SelectItem value="90">90 days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-brand hover:bg-brand-dark text-white rounded-md" data-testid="submit-assessment-button">
                      Create & Generate Link
                    </Button>
                  </form>
                )}
              </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-neutral-200 p-4">
              <p className="text-xs text-neutral-500 uppercase tracking-wider font-bold">Total</p>
              <p className="text-2xl font-bold text-neutral-900 mt-1">{stats.total}</p>
            </div>
            <div className="bg-white rounded-lg border border-neutral-200 p-4">
              <p className="text-xs text-neutral-500 uppercase tracking-wider font-bold">Completed</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{stats.completed}</p>
            </div>
            <div className="bg-white rounded-lg border border-neutral-200 p-4">
              <p className="text-xs text-neutral-500 uppercase tracking-wider font-bold">Pending</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">{stats.pending}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <Input placeholder="Search by name or role..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="max-w-xs rounded-md" data-testid="search-input" />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px] rounded-md" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterVersion} onValueChange={setFilterVersion}>
              <SelectTrigger className="w-[160px] rounded-md" data-testid="filter-version"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Versions</SelectItem>
                <SelectItem value="junior">Junior / Mid</SelectItem>
                <SelectItem value="senior">Senior / Leadership</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 ml-auto">
              <CalendarDays className="h-4 w-4 text-neutral-400" />
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[140px] rounded-md text-sm" data-testid="date-from" placeholder="From" />
              <span className="text-neutral-400 text-xs">to</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[140px] rounded-md text-sm" data-testid="date-to" placeholder="To" />
              {(dateFrom || dateTo) && (
                <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-neutral-400 hover:text-neutral-600" data-testid="clear-dates">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-neutral-200 shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-neutral-500">No assessments found.</TableCell></TableRow>
                ) : filtered.map((a) => (
                  <TableRow key={a.id} data-testid={`assessment-row-${a.id}`}>
                    <TableCell>
                      {a.status === 'completed' && (
                        <Checkbox
                          checked={selectedIds.includes(a.id)}
                          onCheckedChange={() => toggleSelect(a.id)}
                          data-testid={`select-${a.id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{a.candidate_name}</TableCell>
                    <TableCell>{a.role_applied_for}</TableCell>
                    <TableCell><span className="text-xs font-medium">{a.version === 'senior' ? 'Senior' : 'Junior'}</span></TableCell>
                    <TableCell>{getStatusBadge(a.status)}</TableCell>
                    <TableCell className="text-sm text-neutral-500">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        {a.status === 'completed' && (
                          <Button size="sm" onClick={() => navigate(`/report/${a.id}`)} className="bg-brand hover:bg-brand-dark text-white rounded-md" data-testid={`view-report-${a.id}`}>
                            <FileText className="h-3 w-3 mr-1" /> Report
                          </Button>
                        )}
                        {a.status !== 'completed' && a.status !== 'expired' && (
                          <Button size="sm" variant="outline" onClick={() => copyLink(`${window.location.origin}/assess/${a.invite_token}`)} className="rounded-md" data-testid={`copy-link-${a.id}`}>
                            <Copy className="h-3 w-3 mr-1" /> Link
                          </Button>
                        )}
                        {user?.role === 'hr_admin' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteTarget(a)}
                            title={`Delete ${a.candidate_name}`}
                            aria-label={`Delete ${a.candidate_name}`}
                            className="rounded-md text-[#B4552F] border-[#B4552F]/30 hover:bg-[#B4552F]/10 hover:text-[#B4552F] px-2"
                            data-testid={`delete-${a.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
      {/* Confirm before deleting - this hides a candidate's whole record */}
      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="rounded-md" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete this assessment?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#5C665F]">
            <p>
              <span className="font-medium text-[#1A231C]">{deleteTarget?.candidate_name}</span>
              {deleteTarget?.role_applied_for ? ` - ${deleteTarget.role_applied_for}` : ''}
            </p>
            <p>
              It will be removed from this list and the candidate's link will stop working.
              {deleteTarget?.status === 'completed' && ' Their report will no longer be viewable.'}
            </p>
            <p className="text-xs">
              The record is kept for the audit log rather than destroyed, so this can be
              undone if it was a mistake.
            </p>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" className="rounded-md" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              className="bg-[#B4552F] hover:bg-[#93441F] text-white rounded-md"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-button"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Dashboard;
